
import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  authHeaders,
  getCurrentUser,
  handleUnauthorized,
} from "@/lib/auth";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const sections = [
  {
    key: "daily_operations",
    title: "Daily Operations",
    description: "Leads, outreach, follow-ups, and daily tasks.",
    status: "Documentation",
    defaultUrl: "/admin/leads",
    instructions:
      "1. Open Admin Leads to review new restaurant inquiries.\n2. Check the restaurant details and Instagram or website information.\n3. Prepare the restaurant mockup and review its accuracy.\n4. Follow the outreach schedule and record progress.\n5. Verify payment and complete activation before handing over the client dashboard.",
  },
  {
    key: "restaurant_pages",
    title: "Restaurant Pages & Mockups",
    description: "Mockups, campaigns, previews, and restaurant pages.",
    status: "Documentation",
    defaultUrl: "/admin/mockups",
    instructions:
      "1. Open Admin Restaurant Pages.\n2. Create or select a restaurant page.\n3. Enter restaurant details, contact information, photos, and links.\n4. Configure an Offer, Event, or Featured Item.\n5. Review the customer preview before sending it to the owner.\n6. Save, verify the public page, and record outreach progress.",
  },
  {
    key: "payments_activation",
    title: "Payments & Activation",
    description: "Stripe, subscriptions, verification, and handoff.",
    status: "Documentation",
    defaultUrl: "https://dashboard.stripe.com/",
    instructions:
      "First restaurant location: $59 per month.\nAdditional location under the same ownership: $39 per month, with a separate login and dashboard.\n\n1. Verify the restaurant owner's payment in Stripe.\n2. Confirm the correct restaurant record in Admin.\n3. Mark the restaurant paid and complete the activation checklist.\n4. Verify the customer dashboard and live restaurant page.\n5. Send the activation email and verify the recipient address.\n\nDo not assume visiting the activation-success page proves successful payment.",
  },
  {
    key: "emails_automation",
    title: "Emails & Automation",
    description: "Client onboarding, activation, and reminders.",
    status: "Documentation",
    defaultUrl: "/admin/client-emails",
    instructions:
      "Use Admin Client Emails to review the six onboarding templates, their sequence, timing, and enabled settings.\n\nUse the restaurant's activation checklist to manage the customer reminder address and activation communication.\n\nSuccess Reminder delivery and individual test emails have been checked. Actual scheduled onboarding delivery still requires independent verification.\n\nClient training: https://taptotable.co/training",
  },
  {
    key: "prospecting_engine",
    title: "Prospecting Engine",
    description: "Restaurant research, prospect records, and outreach.",
    status: "Documentation",
    defaultUrl: "",
    instructions:
      "Use the prospecting tools to identify potential restaurant clients.\n\n1. Review restaurant information and Instagram or other social links.\n2. Check for existing records before creating a duplicate.\n3. Generate a restaurant preview or mockup.\n4. Review its accuracy before outreach.\n5. Prepare your initial DM or other permitted contact method.\n6. Track follow-ups and prospect status.\n\nAdd the verified direct engine URL in the Access Link field.",
  },
  {
    key: "video_studio",
    title: "Video Studio",
    description: "Promotional reels, scene editing, and recording.",
    status: "Documentation",
    defaultUrl: "",
    instructions:
      "Use TapToTable Video Studio to create promotional reels.\n\n1. Start a project and select the appropriate video format.\n2. Build the reel scene by scene.\n3. Add restaurant visuals, overlays, and narration.\n4. Review scene order and visual consistency.\n5. Record using the established screen recording workflow.\n6. Export or prepare the final recording for publishing.\n\nThe Video Run feature has not been working; use scene-by-scene review instead.\n\nAdd the verified Studio URL in the Access Link field.",
  },
  {
    key: "seo_engine",
    title: "SEO Engine",
    description: "SEO tools and future optimization workflows.",
    status: "In Development",
    defaultUrl: "",
    instructions:
      "This engine is not yet fully tested or documented.\n\nAdd its verified access URL, current capabilities, setup procedure, and usage instructions as development progresses.\n\nDo not treat unfinished features as production-ready.",
  },
  {
    key: "paid_ads",
    title: "Paid Ads",
    description: "Advertising campaigns and performance tracking.",
    status: "In Development",
    defaultUrl: "/admin/paid-ads",
    instructions:
      "Open the Paid Ads admin tool to review available campaign tracking functions.\n\nDocument campaign setup, Meta account connections, budgets, creative assets, tracking, and reporting as these functions are completed.\n\nThis system is not yet fully verified for production use.",
  },
  {
    key: "other_admin_tools",
    title: "Other Admin Tools",
    description: "Success Center, generators, and other utilities.",
    status: "Documentation",
    defaultUrl: "/admin/success-center",
    instructions:
      "Use this section as a directory for additional TapToTable tools.\n\nInclude each tool's name, purpose, direct link, simple instructions, and development status.\n\nKeep unfinished features identified as In Development.",
  },
  {
    key: "technical_reference",
    title: "Technical Reference",
    description: "Hosting, system links, maintenance, and troubleshooting.",
    status: "Documentation",
    defaultUrl: "https://vercel.com/dashboard",
    instructions:
      "Main website: https://taptotable.co\nFrontend hosting: Vercel\nBackend hosting: Render\nDatabase: MongoDB Atlas\nPayments: Stripe\n\nVerify deployment status after code changes.\n\nNever store passwords, API keys, database credentials, or private customer dashboard tokens in this manual.\n\nPrelaunch security: complete Vercel two-factor authentication and securely save recovery codes.",
  },
];

function createInitialRecords() {
  return Object.fromEntries(
    sections.map((section) => [
      section.key,
      {
        instructions: section.instructions,
        access_url: section.defaultUrl,
        notes: "",
      },
    ])
  );
}

function isAllowedLink(value) {
  if (!value) return true;
  if (value.startsWith("/") && !value.startsWith("//")) return true;

  try {
    const parsed = new URL(value);
    return ["http:", "https:"].includes(parsed.protocol);
  } catch {
    return false;
  }
}

export default function AdminManual() {
  const user = getCurrentUser();
  const authorized =
    user && ["owner", "admin"].includes(user.role);

  const [search, setSearch] = useState("");
  const [records, setRecords] = useState(createInitialRecords);
  const [expanded, setExpanded] = useState({});
  const [editing, setEditing] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (!authorized) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function loadManual() {
      try {
        const res = await fetch(
          `${API_BASE}/api/admin/operations-manual`,
          { headers: authHeaders() }
        );

        if (res.status === 401 || res.status === 403) {
          handleUnauthorized();
          return;
        }

        if (!res.ok) {
          throw new Error("Could not load saved manual content.");
        }

        const data = await res.json();

        if (cancelled) return;

        setRecords((current) => {
          const next = { ...current };

          for (const item of data.sections || []) {
            if (!Object.prototype.hasOwnProperty.call(next, item.section_key)) {
              continue;
            }

            next[item.section_key] = {
              instructions: item.instructions ?? "",
              access_url: item.access_url ?? "",
              notes: item.notes ?? "",
            };
          }

          return next;
        });
      } catch (error) {
        if (!cancelled) {
          setMessage(
            "Could not load saved manual content. Editing is unavailable until the connection is restored."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadManual();

    return () => {
      cancelled = true;
    };
  }, [authorized]);

  function updateField(key, field, value) {
    setRecords((current) => ({
      ...current,
      [key]: {
        ...current[key],
        [field]: value,
      },
    }));
  }

  function toggleSection(key) {
    setExpanded((current) => ({
      ...current,
      [key]: !current[key],
    }));
  }

  async function saveSection(section) {
    const record = records[section.key];

    if (!isAllowedLink(record.access_url.trim())) {
      setMessage(
        "Access Link must be a valid HTTPS/HTTP address or an internal path beginning with /."
      );
      return;
    }

    setSavingKey(section.key);
    setMessage("");

    try {
      const res = await fetch(
        `${API_BASE}/api/admin/operations-manual/${section.key}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            ...authHeaders(),
          },
          body: JSON.stringify({
            instructions: record.instructions,
            access_url: record.access_url.trim(),
            notes: record.notes,
          }),
        }
      );

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(
          typeof data.detail === "string"
            ? data.detail
            : "The section could not be saved."
        );
      }

      setRecords((current) => ({
        ...current,
        [section.key]: {
          instructions: data.instructions ?? record.instructions,
          access_url: data.access_url ?? record.access_url,
          notes: data.notes ?? record.notes,
        },
      }));

      setEditing((current) => ({
        ...current,
        [section.key]: false,
      }));

      setMessage(`${section.title} saved successfully.`);
    } catch (error) {
      setMessage(error.message || "Save failed. Please try again.");
    } finally {
      setSavingKey("");
    }
  }

  const query = search.trim().toLowerCase();

  const filteredSections = sections.filter((section) => {
    const record = records[section.key] || {};
    const searchable = [
      section.title,
      section.description,
      section.status,
      record.instructions,
      record.access_url,
      record.notes,
    ]
      .join(" ")
      .toLowerCase();

    return searchable.includes(query);
  });

  if (!authorized) {
    return <Navigate to="/login" replace />;
  }

  return (
    <main className="min-h-screen bg-[#09090B] px-4 py-8 text-white sm:px-8">
      <div className="mx-auto max-w-6xl space-y-7">
        <Link
          to="/admin/mockups"
          className="text-sm font-semibold text-cyan-400 hover:text-cyan-300"
        >
          ← Back to Restaurant Pages
        </Link>

        <header className="space-y-3">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-400">
            TAPTOTABLE ADMIN
          </p>

          <h1 className="text-3xl font-bold sm:text-4xl">
            Operations Manual
          </h1>

          <p className="max-w-3xl text-sm leading-6 text-zinc-400">
            Your central reference for running TapToTable.
            Find procedures, engine links, operational notes,
            and technical instructions in one place.
          </p>
        </header>

        <section className="space-y-2">
          <label
            htmlFor="manual-search"
            className="block text-sm font-semibold text-zinc-300"
          >
            Search Operations Manual
          </label>

          <input
            id="manual-search"
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search tools, instructions, links, and notes..."
            className="w-full rounded-xl border border-white/10 bg-zinc-900 px-4 py-3 text-white placeholder:text-zinc-500 focus:border-cyan-400 focus:outline-none"
          />

          <p className="text-xs text-zinc-500">
            Showing {filteredSections.length} of {sections.length} sections
          </p>
        </section>

        {message && (
          <div
            role="status"
            className="rounded-xl border border-cyan-400/20 bg-zinc-900 p-4 text-sm text-zinc-200"
          >
            {message}
          </div>
        )}

        {loading && (
          <p className="text-sm text-zinc-400">
            Loading saved instructions...
          </p>
        )}

        <div className="grid gap-4 md:grid-cols-2">
          {filteredSections.map((section) => {
            const record = records[section.key];
            const open = Boolean(expanded[section.key]);
            const edit = Boolean(editing[section.key]);
            const validUrl = isAllowedLink(
              (record.access_url || "").trim()
            );

            return (
              <article
                key={section.key}
                className="min-w-0 self-start rounded-2xl border border-white/10 bg-zinc-900/70 p-5"
              >
                <div className="space-y-2">
                  <h2 className="text-lg font-semibold">
                    {section.title}
                  </h2>

                  <p className="text-sm leading-6 text-zinc-400">
                    {section.description}
                  </p>

                  <p
                    className={`text-xs font-semibold ${
                      section.status === "In Development"
                        ? "text-amber-300"
                        : "text-cyan-300"
                    }`}
                  >
                    {section.status}
                  </p>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.key)}
                    className="rounded-xl border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/10"
                  >
                    {open ? "Close" : "Open Instructions"}
                  </button>

                  {record.access_url && validUrl && (
                    <a
                      href={record.access_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="rounded-xl border border-cyan-400/30 px-4 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-400/10"
                    >
                      Open Tool ↗
                    </a>
                  )}
                </div>

                {open && (
                  <div className="mt-5 space-y-5 border-t border-white/10 pt-5">
                    {edit ? (
                      <>
                        <div>
                          <label className="mb-2 block text-sm font-semibold">
                            Instructions
                          </label>
                          <textarea
                            value={record.instructions}
                            onChange={(event) =>
                              updateField(
                                section.key,
                                "instructions",
                                event.target.value
                              )
                            }
                            rows={12}
                            maxLength={30000}
                            className="w-full rounded-xl border border-white/15 bg-zinc-950 p-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold">
                            Access Link
                          </label>
                          <input
                            value={record.access_url}
                            onChange={(event) =>
                              updateField(
                                section.key,
                                "access_url",
                                event.target.value
                              )
                            }
                            maxLength={2000}
                            placeholder="https://... or /admin/..."
                            className="w-full rounded-xl border border-white/15 bg-zinc-950 p-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
                          />
                        </div>

                        <div>
                          <label className="mb-2 block text-sm font-semibold">
                            Owner Notes
                          </label>
                          <textarea
                            value={record.notes}
                            onChange={(event) =>
                              updateField(
                                section.key,
                                "notes",
                                event.target.value
                              )
                            }
                            rows={5}
                            maxLength={10000}
                            placeholder="Add updates, reminders, or known limitations..."
                            className="w-full rounded-xl border border-white/15 bg-zinc-950 p-3 text-sm text-white focus:border-cyan-400 focus:outline-none"
                          />
                        </div>

                        <button
                          type="button"
                          onClick={() => saveSection(section)}
                          disabled={loading || Boolean(savingKey)}
                          className="rounded-xl bg-cyan-400 px-5 py-3 text-sm font-bold text-zinc-950 disabled:opacity-50"
                        >
                          {savingKey === section.key
                            ? "Saving..."
                            : "Save Section"}
                        </button>
                      </>
                    ) : (
                      <>
                        <div>
                          <h3 className="mb-2 text-sm font-bold text-cyan-300">
                            Basic Instructions
                          </h3>
                          <p className="whitespace-pre-wrap break-words text-sm leading-7 text-zinc-300">
                            {record.instructions || "No instructions added yet."}
                          </p>
                        </div>

                        {record.notes && (
                          <div>
                            <h3 className="mb-2 text-sm font-bold text-cyan-300">
                              Owner Notes
                            </h3>
                            <p className="whitespace-pre-wrap break-words text-sm leading-7 text-zinc-300">
                              {record.notes}
                            </p>
                          </div>
                        )}

                        <button
                          type="button"
                          disabled={loading}
                          onClick={() =>
                            setEditing((current) => ({
                              ...current,
                              [section.key]: true,
                            }))
                          }
                          className="rounded-xl border border-amber-400/40 px-4 py-2 text-sm font-semibold text-amber-300 hover:bg-amber-400/10 disabled:opacity-50"
                        >
                          Edit Instructions
                        </button>
                      </>
                    )}
                  </div>
                )}
              </article>
            );
          })}
        </div>

        {filteredSections.length === 0 && (
          <div className="rounded-2xl border border-white/10 p-8 text-center">
            <p className="font-semibold">No matching sections</p>
            <button
              type="button"
              onClick={() => setSearch("")}
              className="mt-4 rounded-xl bg-cyan-400 px-5 py-2 text-sm font-bold text-zinc-950"
            >
              Clear Search
            </button>
          </div>
        )}

        <p className="text-xs leading-6 text-zinc-500">
          Saved section content is stored in TapToTable's database.
          Some tools are still in development. Verify all access
          links and procedures before relying on them in production.
          Never enter passwords or API credentials into this manual.
        </p>
      </div>
    </main>
  );
}
